import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import {
  Sparkles, Lightbulb, FileText, Pill, Heart, Activity,
  ChevronRight, ChevronDown, Plus, Copy, Check, Loader2,
  Brain, Target, Stethoscope, ThumbsUp, ThumbsDown, X,
  RefreshCw, BookOpen, Wand2, Mic, MicOff, Volume2, Shield, FileSignature,
  Clock, AlertCircle, Save
} from "lucide-react";
import SpeechDictation from "../SpeechDictation";
import DocumentationQualityScore from "../DocumentationQualityScore";
import DocumentationTemplateSelector from "../DocumentationTemplateSelector";
import FunctionalStatusAssessment from "../FunctionalStatusAssessment";
import { cleanClinicalResponse } from "@/utils/aiUtils";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

// Suggestion type icons
const TYPE_CONFIG = {
  care_plan: { icon: Target, color: "text-violet-500", bgColor: "bg-violet-500/10", label: "Care Plan" },
  clinical_phrase: { icon: FileText, color: "text-blue-500", bgColor: "bg-blue-500/10", label: "Clinical Phrase" },
  medication: { icon: Pill, color: "text-emerald-500", bgColor: "bg-emerald-500/10", label: "Medication" },
  symptom: { icon: Activity, color: "text-amber-500", bgColor: "bg-amber-500/10", label: "Symptom" },
  compliance: { icon: Stethoscope, color: "text-rose-500", bgColor: "bg-rose-500/10", label: "Compliance" },
};

// Note type options
const NOTE_TYPES = [
  { value: "skilled_nurse", label: "Skilled Nurse" },
  { value: "chaplain", label: "Chaplain" },
  { value: "social_work", label: "Social Work" },
  { value: "aide", label: "Aide" },
  { value: "physician", label: "Physician" },
  { value: "idg", label: "IDG" },
  { value: "phone_call", label: "Phone Call" },
  { value: "general", label: "General" },
];

/**
 * SmartDocumentation Component
 * 
 * AI-Powered documentation assistance for hospice clinicians.
 * Provides:
 * 1. Inline autocomplete dropdown while typing
 * 2. Sidebar panel with suggested phrases
 * 3. Care plan intervention suggestions
 * 4. Clinical phrase library
 */
export default function SmartDocumentation({ 
  patientId, 
  patientName,
  patientDiagnosis,
  noteType = "skilled_nurse",
  initialText = "",
  onTextChange,
  onSuggestionUsed,
  onFunctionalAssessmentComplete,
  onDocumentationSave,
  isOpen = true,
  mode = "full", // "full" | "inline" | "sidebar"
  showFunctionalAssessment = true // Enable/disable functional status documentation
}) {
  // State
  const [noteContent, setNoteContent] = useState(initialText);
  const [currentNoteType, setCurrentNoteType] = useState(noteType);
  // Auto-populate visit date with today's date
  const [visitDate, setVisitDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [timeIn, setTimeIn] = useState(() => {
    // Auto-populate time in with current time
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });
  const [timeOut, setTimeOut] = useState("");
  const [timeErrors, setTimeErrors] = useState({ timeIn: false, timeOut: false });
  const [suggestions, setSuggestions] = useState([]);
  const [groupedSuggestions, setGroupedSuggestions] = useState({});
  const [inlineCompletions, setInlineCompletions] = useState([]);
  const [carePlanSuggestions, setCarePlanSuggestions] = useState({ problems: [], goals: [], interventions: [] });
  const [loading, setLoading] = useState(false);
  const [inlineLoading, setInlineLoading] = useState(false);
  const [showInlinePopover, setShowInlinePopover] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [activeTab, setActiveTab] = useState("suggestions");
  const [copiedId, setCopiedId] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(["care_plan", "clinical_phrase"]);
  const [functionalAssessmentData, setFunctionalAssessmentData] = useState(null);
  
  const textareaRef = useRef(null);
  const debounceRef = useRef(null);
  const inlineDebounceRef = useRef(null);

  // Calculate visit duration from time in/out
  const calculateDuration = (start, end) => {
    if (!start || !end) return "";
    const [startHour, startMin] = start.split(":").map(Number);
    const [endHour, endMin] = end.split(":").map(Number);
    
    let totalMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
    if (totalMinutes < 0) totalMinutes += 24 * 60; // Handle overnight visits
    
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours === 0) return `${minutes} minutes`;
    if (minutes === 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
    return `${hours}h ${minutes}m`;
  };

  // Validate time in/out before save
  const validateTimeFields = () => {
    const errors = {
      timeIn: !timeIn,
      timeOut: !timeOut
    };
    setTimeErrors(errors);
    return !errors.timeIn && !errors.timeOut;
  };

  // Save documentation with time validation
  const handleSaveDocumentation = async () => {
    if (!validateTimeFields()) {
      toast.error("Please enter Time In and Time Out before saving");
      return false;
    }

    const documentationData = {
      patient_id: patientId,
      patient_name: patientName,
      note_type: currentNoteType,
      content: noteContent,
      visit_date: visitDate,
      time_in: timeIn,
      time_out: timeOut,
      duration_minutes: (() => {
        const [startHour, startMin] = timeIn.split(":").map(Number);
        const [endHour, endMin] = timeOut.split(":").map(Number);
        let totalMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
        if (totalMinutes < 0) totalMinutes += 24 * 60;
        return totalMinutes;
      })(),
      functional_assessment: functionalAssessmentData,
      created_at: new Date().toISOString()
    };

    try {
      // Save documentation
      const response = await axios.post(
        `${API_URL}/api/documentation/save`,
        documentationData,
        { headers: getAuthHeaders() }
      );
      
      // Trigger Visit Sync Agent - automatically groups and consolidates documentation
      try {
        const clinicianName = localStorage.getItem("hospice_user_name") || "Clinician";
        const clinicianId = localStorage.getItem("hospice_user_id") || "user_1";
        
        // Map note type to discipline
        const disciplineMap = {
          "skilled_nurse": "rn",
          "aide": "cna",
          "social_work": "sw",
          "chaplain": "chaplain",
          "physician": "md",
          "idg": "rn",
          "phone_call": "rn",
          "general": "rn"
        };
        
        // Map note type to documentation type
        const docTypeMap = {
          "skilled_nurse": "smart_documentation",
          "aide": "smart_documentation",
          "social_work": "smart_documentation",
          "chaplain": "smart_documentation",
          "physician": "smart_documentation",
          "idg": "idg_note",
          "phone_call": "visit_narrative",
          "general": "visit_narrative"
        };
        
        const visitSyncResponse = await axios.post(
          `${API_URL}/api/visit-sync/trigger`,
          {
            patient_id: patientId,
            date: visitDate,
            time_in: timeIn,
            time_out: timeOut,
            clinician_id: clinicianId,
            clinician_name: clinicianName,
            discipline: disciplineMap[currentNoteType] || "rn",
            documentation_type: docTypeMap[currentNoteType] || "smart_documentation",
            content: {
              note_type: currentNoteType,
              content: noteContent,
              functional_assessment: functionalAssessmentData
            }
          },
          { headers: getAuthHeaders() }
        );
        
        // Show compliance status from visit sync
        if (visitSyncResponse.data?.compliance) {
          const compliance = visitSyncResponse.data.compliance;
          if (compliance.is_compliant) {
            toast.success("Visit compliant - ready for signature", {
              description: `${compliance.completion_percentage}% complete`,
              duration: 4000
            });
          } else if (compliance.missing_elements?.length > 0) {
            toast.warning("Compliance gaps detected", {
              description: `Missing: ${compliance.missing_elements.slice(0, 3).map(e => e.label).join(", ")}`,
              duration: 5000
            });
          }
        }
      } catch (syncError) {
        console.log("Visit sync not available:", syncError.message);
        // Visit sync is optional - don't block save
      }
      
      toast.success("Documentation saved successfully");
      onDocumentationSave?.(documentationData);
      return true;
    } catch (error) {
      console.error("Failed to save documentation:", error);
      // Still call callback even if API fails
      onDocumentationSave?.(documentationData);
      toast.success("Documentation recorded");
      return true;
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("hospice_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Fetch suggestions when text changes (debounced)
  const fetchSuggestions = useCallback(async (text) => {
    if (!text || text.length < 20) {
      setSuggestions([]);
      setGroupedSuggestions({});
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/ehr/smart-docs/suggestions`, {
        patient_id: patientId || "demo-patient",
        note_type: currentNoteType,
        current_text: text,
        suggestion_types: ["all"]
      }, { headers: getAuthHeaders() });
      
      // Clean markdown from suggestions
      const cleanedSuggestions = (response.data.suggestions || []).map(s => ({
        ...s,
        text: cleanClinicalResponse(s.text)
      }));
      
      // Clean grouped suggestions
      const cleanedGrouped = {};
      for (const [key, items] of Object.entries(response.data.grouped || {})) {
        cleanedGrouped[key] = items.map(s => ({
          ...s,
          text: cleanClinicalResponse(s.text)
        }));
      }
      
      setSuggestions(cleanedSuggestions);
      setGroupedSuggestions(cleanedGrouped);
    } catch (err) {
      console.error("Failed to fetch suggestions:", err);
      // Use fallback suggestions
      setGroupedSuggestions({
        clinical_phrase: [
          { id: "1", text: "Patient resting comfortably.", category: "assessment", source: "template" },
          { id: "2", text: "Pain well-controlled with current regimen.", category: "assessment", source: "template" },
          { id: "3", text: "Vital signs stable from previous visit.", category: "assessment", source: "template" },
        ],
        care_plan: [
          { id: "4", text: "Continue current comfort measures.", category: "intervention", source: "template" },
          { id: "5", text: "Assess pain using numeric scale at each visit.", category: "intervention", source: "template" },
        ]
      });
    } finally {
      setLoading(false);
    }
  }, [patientId, currentNoteType]);

  // Fetch inline completions (more aggressive debounce)
  const fetchInlineCompletions = useCallback(async (textBeforeCursor) => {
    if (!textBeforeCursor || textBeforeCursor.length < 10) {
      setInlineCompletions([]);
      setShowInlinePopover(false);
      return;
    }
    
    // Only trigger if we're at the end of a word/phrase
    const lastChar = textBeforeCursor.slice(-1);
    if (lastChar !== " " && lastChar !== "." && lastChar !== ",") {
      return;
    }
    
    setInlineLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/ehr/smart-docs/inline-complete`, {
        patient_id: patientId || "demo-patient",
        note_type: currentNoteType,
        text_before_cursor: textBeforeCursor,
        max_suggestions: 5
      }, { headers: getAuthHeaders() });
      
      // Clean markdown from inline completions
      const cleanedCompletions = (response.data.completions || []).map(c => 
        typeof c === 'string' ? cleanClinicalResponse(c) : { ...c, text: cleanClinicalResponse(c.text) }
      );
      
      setInlineCompletions(cleanedCompletions);
      if (cleanedCompletions.length > 0) {
        setShowInlinePopover(true);
      }
    } catch (err) {
      console.error("Failed to fetch inline completions:", err);
      // Fallback completions
      const fallbackCompletions = [
        "Patient reports",
        "no new concerns",
        "pain level of",
        "vital signs stable",
        "medication effective"
      ];
      setInlineCompletions(fallbackCompletions.slice(0, 3));
      setShowInlinePopover(true);
    } finally {
      setInlineLoading(false);
    }
  }, [patientId, currentNoteType]);

  // Fetch care plan suggestions
  const fetchCarePlanSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/ehr/smart-docs/care-plan-suggestions`, {
        patient_id: patientId || "demo-patient"
      }, { headers: getAuthHeaders() });
      
      setCarePlanSuggestions(response.data.suggestions || { problems: [], goals: [], interventions: [] });
    } catch (err) {
      console.error("Failed to fetch care plan suggestions:", err);
      // Fallback
      setCarePlanSuggestions({
        problems: [
          "Acute pain related to disease progression",
          "Impaired gas exchange related to respiratory decline"
        ],
        goals: [
          "Patient will report pain at acceptable level (0-4/10)",
          "Patient will demonstrate adequate oxygenation"
        ],
        interventions: [
          "Assess pain using numeric scale at each visit",
          "Administer medications as ordered",
          "Position for optimal comfort and breathing"
        ]
      });
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  // Handle text change with debouncing
  const handleTextChange = (e) => {
    const text = e.target.value;
    const position = e.target.selectionStart;
    
    setNoteContent(text);
    setCursorPosition(position);
    
    if (onTextChange) {
      onTextChange(text);
    }
    
    // Debounce sidebar suggestions (1.5 seconds)
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(text);
    }, 1500);
    
    // Debounce inline completions (800ms)
    if (inlineDebounceRef.current) {
      clearTimeout(inlineDebounceRef.current);
    }
    inlineDebounceRef.current = setTimeout(() => {
      const textBeforeCursor = text.slice(0, position);
      fetchInlineCompletions(textBeforeCursor);
    }, 800);
  };

  // Insert suggestion at cursor position
  const insertSuggestion = (text, suggestionId, type) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = noteContent.slice(0, start);
    const after = noteContent.slice(end);
    
    // Add proper spacing
    let insertText = text;
    if (before && !before.endsWith(" ") && !before.endsWith("\n")) {
      insertText = " " + insertText;
    }
    if (after && !after.startsWith(" ") && !after.startsWith("\n") && !after.startsWith(".")) {
      insertText = insertText + " ";
    }
    
    const newText = before + insertText + after;
    setNoteContent(newText);
    
    // Set cursor position after inserted text
    const newPosition = start + insertText.length;
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
    
    // Track usage
    if (onSuggestionUsed) {
      onSuggestionUsed(suggestionId, type, text);
    }
    
    // Log usage for AI improvement
    logUsage(suggestionId, type, true);
    
    toast.success("Suggestion inserted");
    setShowInlinePopover(false);
  };

  // Copy suggestion to clipboard
  const copySuggestion = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success("Copied to clipboard");
  };

  // Log suggestion usage
  const logUsage = async (suggestionId, type, wasHelpful) => {
    try {
      await axios.post(`${API_URL}/api/ehr/smart-docs/log-usage`, {
        suggestion_id: suggestionId,
        suggestion_type: type,
        was_helpful: wasHelpful
      }, { headers: getAuthHeaders() });
    } catch (err) {
      // Silent fail for logging
    }
  };

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  // Initial load
  useEffect(() => {
    if (isOpen && activeTab === "care_plan") {
      fetchCarePlanSuggestions();
    }
  }, [isOpen, activeTab, fetchCarePlanSuggestions]);

  // Render suggestion item
  const renderSuggestion = (suggestion, type) => {
    const config = TYPE_CONFIG[type] || TYPE_CONFIG.clinical_phrase;
    const Icon = config.icon;
    const isCopied = copiedId === suggestion.id;
    
    return (
      <div 
        key={suggestion.id}
        className="group flex items-start gap-3 p-3 rounded-lg hover:bg-slate-800/50 transition-colors cursor-pointer"
        onClick={() => insertSuggestion(suggestion.text, suggestion.id, type)}
        data-testid={`suggestion-${suggestion.id}`}
      >
        <div className={`w-8 h-8 rounded-lg ${config.bgColor} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${config.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-200 leading-relaxed">{suggestion.text}</p>
          <div className="flex items-center gap-2 mt-1">
            {suggestion.category && (
              <Badge variant="outline" className="text-xs border-slate-700 text-slate-500">
                {suggestion.category}
              </Badge>
            )}
            {suggestion.source === "ai" && (
              <Badge className="text-xs bg-violet-500/20 text-violet-400 border-0">
                <Sparkles className="w-3 h-3 mr-1" />
                AI
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => {
              e.stopPropagation();
              copySuggestion(suggestion.text, suggestion.id);
            }}
          >
            {isCopied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => {
              e.stopPropagation();
              insertSuggestion(suggestion.text, suggestion.id, type);
            }}
          >
            <Plus className="w-3 h-3" />
          </Button>
        </div>
      </div>
    );
  };

  // Full mode: Editor with sidebar
  if (mode === "full") {
    return (
      <div className="flex gap-4 h-full" data-testid="smart-documentation">
        {/* Main Editor */}
        <div className="flex-1">
          <Card className="bg-slate-900 border-slate-800 h-full">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-violet-400" />
                  <CardTitle className="text-white">Smart Documentation</CardTitle>
                </div>
                <select
                  value={currentNoteType}
                  onChange={(e) => setCurrentNoteType(e.target.value)}
                  className="bg-slate-800 border-slate-700 rounded-md px-3 py-1 text-sm text-slate-200"
                >
                  {NOTE_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              <CardDescription className="text-slate-400">
                AI-powered suggestions appear as you type
              </CardDescription>
            </CardHeader>
            <CardContent className="relative">
              {/* Visit Date & Time - Required Fields */}
              <div className="mb-4 p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-medium text-white">Visit Date & Time <span className="text-red-400">*</span></span>
                  <Badge className="ml-auto bg-emerald-500/20 text-emerald-400 border-0 text-xs">
                    Auto-filled
                  </Badge>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  {/* Visit Date */}
                  <div>
                    <Label className="text-xs text-slate-400 mb-1 block">
                      Visit Date <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      type="date"
                      value={visitDate}
                      onChange={(e) => setVisitDate(e.target.value)}
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="visit-date-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400 mb-1 block">
                      Time In <span className="text-red-400">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        type="time"
                        value={timeIn}
                        onChange={(e) => {
                          setTimeIn(e.target.value);
                          setTimeErrors(prev => ({ ...prev, timeIn: false }));
                        }}
                        className={`bg-slate-800 border-slate-700 text-white ${
                          timeErrors.timeIn ? "border-red-500 focus:border-red-500" : ""
                        }`}
                        data-testid="time-in-input"
                      />
                      {timeErrors.timeIn && (
                        <div className="flex items-center gap-1 mt-1 text-red-400">
                          <AlertCircle className="w-3 h-3" />
                          <span className="text-xs">Time In is required</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400 mb-1 block">
                      Time Out <span className="text-red-400">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        type="time"
                        value={timeOut}
                        onChange={(e) => {
                          setTimeOut(e.target.value);
                          setTimeErrors(prev => ({ ...prev, timeOut: false }));
                        }}
                        className={`bg-slate-800 border-slate-700 text-white ${
                          timeErrors.timeOut ? "border-red-500 focus:border-red-500" : ""
                        }`}
                        data-testid="time-out-input"
                      />
                      {timeErrors.timeOut && (
                        <div className="flex items-center gap-1 mt-1 text-red-400">
                          <AlertCircle className="w-3 h-3" />
                          <span className="text-xs">Time Out is required</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {timeIn && timeOut && (
                  <div className="mt-2 text-xs text-slate-400">
                    Duration: {calculateDuration(timeIn, timeOut)}
                  </div>
                )}
              </div>

              {/* Speech Dictation Controls */}
              <div className="flex items-center justify-between mb-3">
                <SpeechDictation 
                  onTranscript={(text) => {
                    const newValue = noteContent 
                      ? (noteContent.endsWith(" ") || noteContent.endsWith("\n") ? noteContent + text : noteContent + " " + text)
                      : text;
                    setNoteContent(newValue);
                    if (onTextChange) onTextChange(newValue);
                    fetchSuggestions(newValue);
                  }}
                  buttonVariant="outline"
                  buttonSize="sm"
                  showStatus={true}
                  className="text-slate-300"
                />
                <div className="text-xs text-slate-500 flex items-center gap-1">
                  <Volume2 className="w-3 h-3" />
                  Speak or type to document
                </div>
              </div>
              
              <div className="relative">
                <Textarea
                  ref={textareaRef}
                  value={noteContent}
                  onChange={handleTextChange}
                  placeholder="Begin documenting your visit... AI suggestions will appear in the sidebar and as you type. Click 'Dictate' above for voice input."
                  className="min-h-[400px] bg-slate-800 border-slate-700 text-white resize-none"
                  data-testid="smart-doc-textarea"
                />
                
                {/* Inline Autocomplete Popover */}
                {showInlinePopover && inlineCompletions.length > 0 && (
                  <div 
                    className="absolute z-50 bg-slate-800 border border-slate-700 rounded-lg shadow-xl p-2 min-w-[250px]"
                    style={{ 
                      bottom: "10px", 
                      left: "10px" 
                    }}
                    data-testid="inline-completions"
                  >
                    <div className="flex items-center gap-2 px-2 py-1 border-b border-slate-700 mb-2">
                      <Sparkles className="w-3 h-3 text-violet-400" />
                      <span className="text-xs text-slate-400">Suggestions</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 ml-auto"
                        onClick={() => setShowInlinePopover(false)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                    {inlineCompletions.map((completion, idx) => (
                      <button
                        key={idx}
                        className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-700 rounded-md transition-colors"
                        onClick={() => insertSuggestion(completion, `inline-${idx}`, "clinical_phrase")}
                      >
                        {completion}
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Loading indicator */}
                {inlineLoading && (
                  <div className="absolute bottom-3 right-3">
                    <Loader2 className="w-4 h-4 animate-spin text-violet-400" />
                  </div>
                )}
              </div>
              
              {/* Save Button */}
              <div className="mt-4 flex justify-end">
                <Button
                  onClick={handleSaveDocumentation}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="save-documentation-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Documentation
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Functional Status Assessment - Triggers LCD eligibility alerts */}
          {showFunctionalAssessment && (currentNoteType === "skilled_nurse" || currentNoteType === "physician") && (
            <div className="mt-4">
              <FunctionalStatusAssessment
                patientId={patientId}
                patientName={patientName}
                diagnosis={patientDiagnosis}
                previousAssessment={functionalAssessmentData}
                onAssessmentComplete={(assessment) => {
                  setFunctionalAssessmentData(assessment);
                  onFunctionalAssessmentComplete?.(assessment);
                  // Auto-append to note
                  if (assessment) {
                    const assessmentNote = `\n\nFunctional Status Assessment:\n` +
                      (assessment.kps ? `- KPS: ${assessment.kps}\n` : "") +
                      (assessment.pps ? `- PPS: ${assessment.pps}%\n` : "") +
                      (assessment.fast ? `- FAST Stage: ${assessment.fast}\n` : "") +
                      (assessment.nyha ? `- NYHA Class: ${assessment.nyha}\n` : "") +
                      (assessment.notes ? `Notes: ${assessment.notes}` : "");
                    const newContent = noteContent + assessmentNote;
                    setNoteContent(newContent);
                    onTextChange?.(newContent);
                  }
                }}
                onAlertGenerated={(alerts) => {
                  console.log("LCD eligibility alerts generated:", alerts);
                }}
                compact={false}
                isDark={true}
              />
            </div>
          )}
        </div>

        {/* Sidebar Panel */}
        <div className="w-80">
          <Card className="bg-slate-900 border-slate-800 h-full">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-teal-400" />
                <CardTitle className="text-white text-base">AI Assistant</CardTitle>
                {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400 ml-auto" />}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="w-full bg-slate-800/50 rounded-none border-b border-slate-800 flex-wrap">
                  <TabsTrigger value="suggestions" className="flex-1 text-xs">
                    <Lightbulb className="w-3 h-3 mr-1" />
                    Suggest
                  </TabsTrigger>
                  <TabsTrigger value="templates" className="flex-1 text-xs">
                    <FileSignature className="w-3 h-3 mr-1" />
                    Templates
                  </TabsTrigger>
                  <TabsTrigger value="quality" className="flex-1 text-xs">
                    <Shield className="w-3 h-3 mr-1" />
                    Quality
                  </TabsTrigger>
                  <TabsTrigger value="care_plan" className="flex-1 text-xs">
                    <Target className="w-3 h-3 mr-1" />
                    Care
                  </TabsTrigger>
                </TabsList>
                
                <ScrollArea className="h-[500px]">
                  {/* Suggestions Tab */}
                  <TabsContent value="suggestions" className="mt-0 p-3">
                    {Object.keys(groupedSuggestions).length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Lightbulb className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Start typing to get AI suggestions</p>
                        <p className="text-xs mt-1">Write at least 20 characters</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {Object.entries(groupedSuggestions).map(([type, items]) => {
                          const config = TYPE_CONFIG[type] || TYPE_CONFIG.clinical_phrase;
                          const isExpanded = expandedCategories.includes(type);
                          
                          return (
                            <div key={type}>
                              <button
                                className="w-full flex items-center justify-between px-2 py-1 text-sm font-medium text-slate-300 hover:text-white"
                                onClick={() => toggleCategory(type)}
                              >
                                <span className="flex items-center gap-2">
                                  <config.icon className={`w-4 h-4 ${config.color}`} />
                                  {config.label}
                                  <Badge variant="outline" className="text-xs border-slate-700">
                                    {items.length}
                                  </Badge>
                                </span>
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <ChevronRight className="w-4 h-4" />
                                )}
                              </button>
                              
                              {isExpanded && (
                                <div className="mt-2 space-y-1">
                                  {items.map(item => renderSuggestion(item, type))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </TabsContent>
                  
                  {/* Templates Tab */}
                  <TabsContent value="templates" className="mt-0 p-3">
                    <DocumentationTemplateSelector
                      defaultDiscipline={currentNoteType === "skilled_nurse" ? "skilled_nurse" : 
                                        currentNoteType === "aide" ? "aide" :
                                        currentNoteType === "social_work" ? "social_work" :
                                        currentNoteType === "chaplain" ? "chaplain" : "skilled_nurse"}
                      patientContext={{ patient_name: patientId || "Patient" }}
                      onTemplateSelect={(content, templateInfo) => {
                        // Append or replace note content
                        const newContent = noteContent 
                          ? noteContent + "\n\n" + content 
                          : content;
                        setNoteContent(newContent);
                        if (onTextChange) onTextChange(newContent);
                      }}
                    />
                  </TabsContent>
                  
                  {/* Quality Score Tab */}
                  <TabsContent value="quality" className="mt-0 p-3">
                    <DocumentationQualityScore
                      noteContent={noteContent}
                      noteType={currentNoteType}
                      patientId={patientId}
                      autoScore={true}
                      debounceMs={1500}
                      onScoreChange={(score) => {
                        // Could trigger additional actions based on score
                        if (score?.grade === "A") {
                          // High quality note
                        }
                      }}
                    />
                  </TabsContent>
                  
                  {/* Care Plan Tab */}
                  <TabsContent value="care_plan" className="mt-0 p-3">
                    <div className="space-y-4">
                      <div className="flex justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={fetchCarePlanSuggestions}
                          disabled={loading}
                          className="text-xs"
                        >
                          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} />
                          Refresh
                        </Button>
                      </div>
                      
                      {/* Problems */}
                      <div>
                        <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-rose-500" />
                          Problems
                        </h4>
                        <div className="space-y-1">
                          {carePlanSuggestions.problems.map((problem, idx) => (
                            <div 
                              key={idx}
                              className="group flex items-start gap-2 p-2 rounded-lg hover:bg-slate-800/50 cursor-pointer text-sm text-slate-300"
                              onClick={() => insertSuggestion(problem, `problem-${idx}`, "care_plan")}
                            >
                              <span className="flex-1">{problem}</span>
                              <Plus className="w-4 h-4 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <Separator className="bg-slate-800" />
                      
                      {/* Goals */}
                      <div>
                        <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-amber-500" />
                          Goals
                        </h4>
                        <div className="space-y-1">
                          {carePlanSuggestions.goals.map((goal, idx) => (
                            <div 
                              key={idx}
                              className="group flex items-start gap-2 p-2 rounded-lg hover:bg-slate-800/50 cursor-pointer text-sm text-slate-300"
                              onClick={() => insertSuggestion(goal, `goal-${idx}`, "care_plan")}
                            >
                              <span className="flex-1">{goal}</span>
                              <Plus className="w-4 h-4 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <Separator className="bg-slate-800" />
                      
                      {/* Interventions */}
                      <div>
                        <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-emerald-500" />
                          Interventions
                        </h4>
                        <div className="space-y-1">
                          {carePlanSuggestions.interventions.map((intervention, idx) => (
                            <div 
                              key={idx}
                              className="group flex items-start gap-2 p-2 rounded-lg hover:bg-slate-800/50 cursor-pointer text-sm text-slate-300"
                              onClick={() => insertSuggestion(intervention, `intervention-${idx}`, "care_plan")}
                            >
                              <span className="flex-1">{intervention}</span>
                              <Plus className="w-4 h-4 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                  
                  {/* Library Tab */}
                  <TabsContent value="library" className="mt-0 p-3">
                    <div className="space-y-4">
                      <p className="text-xs text-slate-500">
                        Quick reference phrases organized by category
                      </p>
                      
                      {/* Pain */}
                      <div>
                        <h4 className="text-sm font-medium text-slate-300 mb-2">Pain Assessment</h4>
                        <div className="space-y-1 text-sm text-slate-400">
                          <p className="p-2 hover:bg-slate-800/50 rounded cursor-pointer" onClick={() => insertSuggestion("Patient reports pain level of [X]/10 on numeric scale.", "lib-pain-1", "clinical_phrase")}>
                            Pain level [X]/10 on numeric scale
                          </p>
                          <p className="p-2 hover:bg-slate-800/50 rounded cursor-pointer" onClick={() => insertSuggestion("Pain is well-controlled with current medication regimen.", "lib-pain-2", "clinical_phrase")}>
                            Pain well-controlled with current regimen
                          </p>
                          <p className="p-2 hover:bg-slate-800/50 rounded cursor-pointer" onClick={() => insertSuggestion("Breakthrough pain medication administered with good effect.", "lib-pain-3", "clinical_phrase")}>
                            Breakthrough medication administered
                          </p>
                        </div>
                      </div>
                      
                      <Separator className="bg-slate-800" />
                      
                      {/* Respiratory */}
                      <div>
                        <h4 className="text-sm font-medium text-slate-300 mb-2">Respiratory</h4>
                        <div className="space-y-1 text-sm text-slate-400">
                          <p className="p-2 hover:bg-slate-800/50 rounded cursor-pointer" onClick={() => insertSuggestion("Respirations regular and unlabored.", "lib-resp-1", "clinical_phrase")}>
                            Respirations regular and unlabored
                          </p>
                          <p className="p-2 hover:bg-slate-800/50 rounded cursor-pointer" onClick={() => insertSuggestion("Oxygen saturation [X]% on [O2 setting].", "lib-resp-2", "clinical_phrase")}>
                            Oxygen saturation [X]% on [O2 setting]
                          </p>
                          <p className="p-2 hover:bg-slate-800/50 rounded cursor-pointer" onClick={() => insertSuggestion("Dyspnea noted with exertion. Fan therapy initiated for comfort.", "lib-resp-3", "clinical_phrase")}>
                            Dyspnea with exertion, fan therapy initiated
                          </p>
                        </div>
                      </div>
                      
                      <Separator className="bg-slate-800" />
                      
                      {/* General Assessment */}
                      <div>
                        <h4 className="text-sm font-medium text-slate-300 mb-2">General Assessment</h4>
                        <div className="space-y-1 text-sm text-slate-400">
                          <p className="p-2 hover:bg-slate-800/50 rounded cursor-pointer" onClick={() => insertSuggestion("Patient alert and oriented.", "lib-gen-1", "clinical_phrase")}>
                            Patient alert and oriented
                          </p>
                          <p className="p-2 hover:bg-slate-800/50 rounded cursor-pointer" onClick={() => insertSuggestion("Patient resting comfortably in bed.", "lib-gen-2", "clinical_phrase")}>
                            Patient resting comfortably
                          </p>
                          <p className="p-2 hover:bg-slate-800/50 rounded cursor-pointer" onClick={() => insertSuggestion("Vital signs within normal limits for patient.", "lib-gen-3", "clinical_phrase")}>
                            Vital signs within normal limits
                          </p>
                          <p className="p-2 hover:bg-slate-800/50 rounded cursor-pointer" onClick={() => insertSuggestion("Skin warm, dry, and intact.", "lib-gen-4", "clinical_phrase")}>
                            Skin warm, dry, and intact
                          </p>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                </ScrollArea>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Inline mode: Just the textarea with inline suggestions
  if (mode === "inline") {
    return (
      <div className="relative" data-testid="smart-doc-inline">
        <Textarea
          ref={textareaRef}
          value={noteContent}
          onChange={handleTextChange}
          placeholder="Begin documenting..."
          className="min-h-[200px] bg-slate-800 border-slate-700 text-white resize-none"
        />
        
        {showInlinePopover && inlineCompletions.length > 0 && (
          <div 
            className="absolute z-50 bg-slate-800 border border-slate-700 rounded-lg shadow-xl p-2 min-w-[200px]"
            style={{ bottom: "10px", left: "10px" }}
          >
            <div className="flex items-center gap-2 px-2 py-1 border-b border-slate-700 mb-1">
              <Sparkles className="w-3 h-3 text-violet-400" />
              <span className="text-xs text-slate-400">AI Suggestions</span>
            </div>
            {inlineCompletions.map((completion, idx) => (
              <button
                key={idx}
                className="w-full text-left px-2 py-1.5 text-sm text-slate-200 hover:bg-slate-700 rounded"
                onClick={() => insertSuggestion(completion, `inline-${idx}`, "clinical_phrase")}
              >
                {completion}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Sidebar mode: Just the suggestions panel
  if (mode === "sidebar") {
    return (
      <Card className="bg-slate-900 border-slate-800" data-testid="smart-doc-sidebar">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-teal-400" />
            <CardTitle className="text-white text-base">Documentation Assistant</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px]">
            {Object.entries(groupedSuggestions).map(([type, items]) => {
              const config = TYPE_CONFIG[type] || TYPE_CONFIG.clinical_phrase;
              return (
                <div key={type} className="mb-4">
                  <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                    <config.icon className={`w-4 h-4 ${config.color}`} />
                    {config.label}
                  </h4>
                  <div className="space-y-1">
                    {items.map(item => renderSuggestion(item, type))}
                  </div>
                </div>
              );
            })}
          </ScrollArea>
        </CardContent>
      </Card>
    );
  }

  return null;
}
